import { createRouter, createWebHashHistory } from 'vue-router';
import { h } from 'vue';

// Helper to safely import dynamic components. If the import fails or the
// module doesn't export a component, return a simple fallback component so
// vue-router never receives `undefined` (which causes the "in" operator
// TypeError seen in the stack trace).
function safeImport(promiseFactory) {
  return async () => {
    try {
      const mod = await promiseFactory();
      // Prefer default export if present
      return mod && (mod.default || mod);
    } catch (err) {
      // Log so we can see the real error in the console and avoid crashing.
      // eslint-disable-next-line no-console
      console.error('Dynamic import failed:', err);
      // Return a render-function based fallback so we don't require the
      // runtime template compiler in this build of Vue.
      return {
        name: 'AsyncImportFallback',
        render() {
          return h(
            'div',
            { style: 'padding:1rem;color:var(--text);' },
            'Failed to load component.'
          );
        },
      };
    }
  };
}

// Route components. Assets and Templates are core and loaded statically.
const Account = safeImport(() => import('./components/Account/Account.vue'));
import Configure from './components/Configure/Configure.vue';
import Templates from './components/Templates/Templates.vue';
import Assets from './components/Assets/Assets.vue';
const OnAir = safeImport(() => import('./components/OnAir/OnAir.vue'));
const OnAirControl = safeImport(
  () => import('./components/OnAirControl/OnAirControl.vue')
);
const Apps = safeImport(() => import('./components/AppViewer/Apps.vue'));
const Players = safeImport(() => import('./components/Players/Players.vue'));
const Channels = safeImport(() => import('./components/Channels/Channels.vue'));
const ChannelDetail = safeImport(
  () => import('./components/Channels/ChannelDetail.vue')
);
const LiveState = safeImport(
  () => import('./components/LiveState/LiveState.vue')
);

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/Assets',
      name: 'Assets',
      component: Assets,
    },
    {
      path: '/Templates',
      name: 'Templates',
      component: Templates,
      props: { filter: 'templates' },
    },
    // {
    //   path: '/Templates/Default',
    //   name: 'Default',
    //   component: Templates,
    //   props: { filter: 'templates' },
    // },
    {
      path: '/Templates/Presets',
      name: 'Presets',
      component: Templates,
      props: { filter: 'presets' },
    },
    {
      path: '/OnAir',
      name: 'OnAir',
      component: OnAir,
    },
    {
      path: '/OnAirControl',
      name: 'OnAirControl',
      component: OnAirControl,
    },
    {
      path: '/Apps/:appname',
      name: 'Apps',
      component: Apps,
    },
    {
      path: '/Configure',
      name: 'Configure',
      component: Configure,
    },
    {
      path: '/Account',
      name: 'Account',
      component: Account,
    },
    {
      path: '/Players',
      name: 'Players',
      component: Players,
    },
    {
      path: '/Channels',
      name: 'Channels',
      component: Channels,
    },
    {
      path: '/Channels/:uid',
      name: 'ChannelDetail',
      component: ChannelDetail,
      props: true,
    },
    {
      path: '/LiveState',
      name: 'LiveState',
      component: LiveState,
    },
    {
      path: '/',
      redirect: '/Assets',
    },
  ],
});

// Minimal guard: warn if a route record has no component to avoid
// vue-router throwing an error deep in its navigation logic.
router.beforeEach((to, from, next) => {
  try {
    const records = router.getRoutes().filter((r) => r.name === to.name);
    for (const rec of records) {
      if (!rec.component && !rec.components) {
        console.warn('Route record has no component:', rec);
      }
    }
  } catch (err) {
    console.warn('Error while checking route records', err);
  }
  return next();
});

export default router;
